import { Module } from 'config';
import routes, {
  getCompanySettingsSectionRoute,
  getProfileDetailRoute,
} from 'constants/routes';
import { getBasePath } from 'utils/url';

const STORAGE_KEY = 'settings_v2_enabled';

type ProfileSettingsTab = 'labels' | 'post-checklist' | 'audience-restrictions';

const LEGACY_PROFILE_PROPERTY: Record<ProfileSettingsTab, string> = {
  labels: '/property:tags',
  'post-checklist': '/property:post-requirements',
  'audience-restrictions': '',
};

/**
 * Lets a development build switch Settings v2 on without the account being on
 * the api allowlist (`SETTINGS_V2_ACCOUNT_IDS`). Production builds ignore it;
 * see `useSettingsV2Gate`. Toggle it at /app/dev/settings-v2 and reload.
 */
export function isSettingsV2DevOverrideOn(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setSettingsV2DevOverride(enabled: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    // ignore — private mode / storage disabled
  }
}

export function profileSettingsLink(
  pageId: number,
  tab: ProfileSettingsTab,
  isSettingsV2Enabled: boolean,
): string | { pathname: string } {
  if (isSettingsV2Enabled) {
    return { pathname: getProfileDetailRoute({ profileId: pageId, tab }) };
  }

  return `${Module.OldApp.URL.SETTINGS.PROFILES}/page:${pageId}/type:my${LEGACY_PROFILE_PROPERTY[tab]}`;
}

export function profileSettingsUrl(
  pageId: number,
  isGlobal: boolean,
  isSettingsV2Enabled: boolean,
): string {
  if (isSettingsV2Enabled) {
    return `${getBasePath()}${getProfileDetailRoute({
      profileId: pageId,
      isGlobalContentManager: isGlobal,
    })}`;
  }

  return `/Projects/Pages/page:${pageId}/type:${isGlobal ? 'global' : 'my'}`;
}

export function hashtagPresetsSettingsLink(
  isCompanySettingsV2Enabled: boolean,
): string | { pathname: string } {
  if (isCompanySettingsV2Enabled) {
    return { pathname: getCompanySettingsSectionRoute('hashtag-presets') };
  }

  return '/dashboard/settings#hashtag-presets';
}

export function companySettingsLink(
  isCompanySettingsV2Enabled: boolean,
): string | { pathname: string } {
  return isCompanySettingsV2Enabled
    ? { pathname: routes.SETTINGS.COMPANY }
    : Module.OldApp.URL.SETTINGS.COMPANY_SETTINGS;
}

export function globalProfilesUrl(isSettingsV2Enabled: boolean): string {
  return isSettingsV2Enabled
    ? `${getBasePath()}${routes.SETTINGS.GLOBAL_PROFILES}`
    : Module.OldApp.URL.SETTINGS.KONTENT_BASE;
}
